// components/GoogleMapByLatLng.tsx
import React, { useEffect, useRef, useState } from "react";
import { Libraries, useLoadScript } from "@react-google-maps/api";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import mapsStyle from "./maps-style.json";
import styles from "./GoogleMap.module.scss";

//import { MarkerClusterer } from "@googlemaps/markerclusterer";

import {
  MarkerClusterer as GoogleMarkerCluster,
  onClusterClickHandler,
  Renderer,
} from "@googlemaps/markerclusterer";
import { useRouter } from "next/router";

type InitMapProps = {
  mapRef: HTMLDivElement;
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  markers:
    | google.maps.MarkerOptions[]
    | {
        options: google.maps.MarkerOptions;
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData: Record<string, any>;
      }[];
  clusterer?:
    | {
        renderer?: Renderer;
        onClusterClick?: onClusterClickHandler;
      }
    | boolean;
};

async function initMap({
  mapRef,
  center,
  zoom,
  markers: markersData,
  clusterer,
}: InitMapProps) {
  // Request needed libraries.
  const { Map } = (await google.maps.importLibrary(
    "maps",
  )) as google.maps.MapsLibrary;
  const { Marker } = (await google.maps.importLibrary(
    "marker",
  )) as google.maps.MarkerLibrary;
  const map = new Map(mapRef, {
    zoom,
    center,
    styles: mapsStyle,
    zoomControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    clickableIcons: false,
    keyboardShortcuts: false,
    gestureHandling: "cooperative",
    ...(clusterer && {
      maxZoom: 15, // this results in same coordinates always being shown as cluster - otherwise single markers (which are above of each other) will be used if you zoom to far in
    }),
  });

  const markers = markersData.map((markerData) => {
    let options: google.maps.MarkerOptions;
    let additionalData = {};
    if ("additionalData" in markerData) {
      options = markerData.options;
      additionalData = markerData.additionalData;
    } else {
      options = markerData;
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const marker: google.maps.Marker & Record<string, any> = new Marker({
      ...options,
      ...(!clusterer && {
        map,
      }),
    });
    for (const [key, value] of Object.entries(additionalData)) {
      marker[key] = value;
    }

    return marker;
  });

  if (clusterer) {
    new GoogleMarkerCluster({
      markers,
      map,
      onClusterClick:
        typeof clusterer === "object" ? clusterer.onClusterClick : undefined,
      renderer: typeof clusterer === "object" ? clusterer.renderer : undefined,
    });
  }

  return { map, markers };
}

type MarkerBase = { lat: number; lng: number; title: string };

const libraries: Libraries = ["places"];
type GoogleMapByLatLngProps<T> = {
  markers?: (MarkerBase & T)[];
  onMarkerClick?: (marker: Omit<T, keyof MarkerBase>) => void;
  clusterer?:
    | {
        clusterLabel: string;
        onClusterClick?: (markers: Omit<T, keyof MarkerBase>[]) => void;
      }
    | false;
  alignment:
    | {
        center: { lat: number; lng: number };
        zoom: number;
      }
    | {
        autoCenterAndZoom: true;
        fallbackCenter: { lat: number; lng: number };
        fallbackZoom: number;
      };
};

const GoogleMapByLatLng = <T,>({
  markers = [],
  clusterer,
  onMarkerClick,
  alignment,
}: GoogleMapByLatLngProps<T>) => {
  const { getIcon } = useGlobalContext();
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const { locale } = useRouter();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || "",
    libraries,
    language: locale,
  });

  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [markerInstances, setMarkerInstances] = useState<google.maps.Marker[]>(
    [],
  );

  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    if (loadError) return;
    if (!mapRef.current) return;
    initMap({
      mapRef: mapRef.current,
      ...("center" in alignment && {
        center: alignment.center,
        zoom: alignment.zoom,
      }),
      ...(markers.length === 0 &&
        "autoCenterAndZoom" in alignment && {
          zoom: alignment.fallbackZoom,
          center: alignment.fallbackCenter,
        }),
      markers: markers.map((m) => {
        const { lat, lng, title, ...rest } = m;
        return {
          options: {
            position: { lat, lng },
            icon: {
              url: getIcon("google-map-marker"),
              scaledSize: new google.maps.Size(40, 40),
            },
            title,
          },
          additionalData: rest,
        };
      }),
      clusterer: clusterer && {
        onClusterClick: (event, cluster /*, map*/) => {
          const markers = cluster.markers as T[];
          if (typeof clusterer === "object") {
            clusterer.onClusterClick?.(markers);
          }
        },
        renderer: {
          render(cluster, stats, map) {
            return new google.maps.Marker({
              position: cluster.bounds?.getCenter(),
              map,
              title: clusterer.clusterLabel.replaceAll(
                "$count",
                cluster.count.toString(),
              ),
              icon: {
                url: getIcon("google-map-cluster"),
                scaledSize: new google.maps.Size(64, 64),
              },
              label: {
                text: cluster.count.toString(),
                color: "#fff",
                fontSize: "16px",
                fontFamily: "display-font-bold",
              },
            });
          },
        },
      },
    }).then(({ map, markers }) => {
      setMapInstance(map);
      setMarkerInstances(markers);
    });
  }, [isLoaded, clusterer, markers, getIcon, loadError, alignment]);

  useEffect(() => {
    if (!mapInstance) return;
    if (
      "autoCenterAndZoom" in alignment &&
      alignment.autoCenterAndZoom &&
      markers.length > 0
    ) {
      const bounds = new google.maps.LatLngBounds();

      // Extend bounds to include all marker positions
      markers.forEach((marker) => {
        bounds.extend(marker);
      });

      // Fit map to bounds
      mapInstance.fitBounds(bounds);
    }
  }, [mapInstance, markers, alignment]);

  useEffect(() => {
    for (const marker of markerInstances) {
      marker.addListener("click", () => {
        onMarkerClick?.(marker as T);
      });
    }
  }, [markerInstances, onMarkerClick]);

  // Hack to disable page scroll behavior when using Cmd/Ctrl + scroll on the map.
  useEffect(() => {
    const mapNode = mapRef.current;
    if (!mapNode) return;

    const handleWheel = (e: WheelEvent) => {
      // If Cmd/Ctrl is held, we want the map to zoom, NOT the page to scroll.
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation(); // Stops the event from hitting the parent containers
      }
    };

    // We attach it to the container ref
    mapNode.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      mapNode.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    // Map is loaded and ready to be displayed
    <div className={styles["google-map-container"]} ref={mapRef}></div>
  );
};

export default GoogleMapByLatLng;
